import React from "react";
import {Helmet} from "react-helmet-async";
import {Link} from "react-router-dom";
import LegalLayout from "../../components/eas/LegalLayout";

/**
 * Special-offer terms for the Effective Action System purchase. Mirrors the PL
 * structure where the main Online Course Terms apply by default, and a separate
 * document carries the EAS-specific provisions (1-1 session, live group meetings,
 * 30-day satisfaction guarantee, community rules). Linked from /system + the
 * webinar purchase page.
 */
export default function LegalEasOfferTermsPage(): JSX.Element {
    return (
        <>
            <Helmet>
                <title>Effective Action System — Special Offer Terms</title>
                <meta
                    name="description"
                    content="Special offer terms applicable to the purchase of the Effective Action System. Supplements the EffectiveActionSystem.com Online Course Terms and Conditions."
                />
                <link rel="canonical" href="https://effectiveactionsystem.com/legal/eas-offer-terms"/>
            </Helmet>
            <LegalLayout title="Effective Action System — Special Offer Terms">
                <p style={{fontStyle: "italic", color: "rgba(255,255,255,0.7)"}}>
                    These special offer terms apply when you purchase the Effective Action System program.
                    They are <strong>not</strong> part of the main {" "}
                    <Link to="/legal/course-terms">Online Course Terms and Conditions</Link>{" "}
                    — they supplement them. In the event of any inconsistency, the provisions of this
                    document take precedence with respect to the Effective Action System.
                </p>

                <h2>1. Scope and Format</h2>
                <ul>
                    <li>Program: 6-week online program, with new modules unlocked every 7 days.</li>
                    <li>Materials: video, audio, PDF materials, and implementation tasks.</li>
                    <li>Community: access to the course forum/group.</li>
                </ul>

                <h2>2. Introductory 1-1 Session</h2>
                <ul>
                    <li>The program includes one introductory 1-1 session.</li>
                    <li>The purpose of the session is to help the Participant start the program, clarify goals, and understand how to use the course materials effectively.</li>
                    <li>The session is not a form of therapy, psychological counselling, medical advice, or individual educational diagnosis.</li>
                    <li>The session should be scheduled within the timeframe indicated in the offer or course platform. If the Participant does not schedule or attend the session within that timeframe, the session may be considered used, unless otherwise agreed by EffectiveActionSystem.com.</li>
                </ul>

                <h2>3. Live Group Meetings</h2>
                <ul>
                    <li>Frequency and duration: once per week, 60 minutes.</li>
                    <li>Recording: meetings are recorded solely for the purposes of Participants of the Effective Action System. Participation means consent to the recording of voice/image to the extent necessary to deliver the course.</li>
                    <li>Access to recordings: recordings are available only to course Participants. Downloading or sharing recordings outside the platform is prohibited.</li>
                </ul>

                <h2>4. Voluntary Satisfaction Guarantee</h2>
                <p><strong>Period:</strong> 30 days from the purchase date.</p>
                <p><strong>Conditions:</strong></p>
                <ul>
                    <li>a) watching all materials from the first 2 weeks,</li>
                    <li>b) attending at least 1 online meeting,</li>
                    <li>c) submitting completed exercises from the first 2 weeks.</li>
                </ul>
                <p><strong>Procedure:</strong> the request should be submitted by email to <a href="mailto:support@effectiveactionsystem.com">support@effectiveactionsystem.com</a>.</p>
                <p>After positive verification, the refund will be made within 7 business days to the same payment method, unless the User indicates another method that does not involve additional costs.</p>
                <p><strong>Exclusion of the guarantee:</strong> detailed rules of the guarantee, including cases where the right to a refund is excluded, are set out in section 7.3 of the EffectiveActionSystem.com Online Courses Terms and Conditions.</p>

                <h2>5. Community Rules</h2>
                <ul>
                    <li><strong>Discussion culture:</strong> Participants are required to show mutual respect. Hate speech, spam, and publishing, downloading, or sharing course materials outside the platform are prohibited.</li>
                    <li><strong>No plagiarism or unfair practices:</strong> it is prohibited, for example, to generate assignments using AI tools if the instructions do not allow it.</li>
                </ul>

                <h2>Final Clause</h2>
                <p>
                    This document supplements the EffectiveActionSystem.com Online Courses Terms and Conditions.
                    For the Effective Action System, the provisions of this document take precedence in the event
                    of any discrepancy with the main Terms and Conditions. In all other respects, the general
                    provisions of the {" "}
                    <Link to="/legal/course-terms">Online Courses Terms and Conditions</Link>, the {" "}
                    <Link to="/legal/terms">main Terms and Conditions</Link>, and the {" "}
                    <Link to="/legal/privacy">Privacy Policy</Link>{" "}
                    available within the EffectiveActionSystem.com service apply.
                </p>

                <p style={{
                    marginTop: "3rem",
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "0.85rem",
                }}>
                    Last updated: May 6, 2026
                </p>
            </LegalLayout>
        </>
    );
}
